import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} HeroRefs
 * @property {HTMLElement} [background] - The wrapper holding the background video or image.
 */

/**
 * Hero: keeps the looping background honest for anyone who asked for less motion.
 *
 * The wordmark that zooms out over this section belongs to the header, which owns
 * both the element and the scroll state behind it — see sections/header.liquid.
 *
 * @extends {Component<HeroRefs>}
 */
class HeroComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const video = this.#video;

    if (!video) return;

    video.addEventListener('play', this.#pauseForReducedMotion);
    this.#pauseForReducedMotion();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#video?.removeEventListener('play', this.#pauseForReducedMotion);
  }

  /**
   * The background video, if the merchant picked one.
   *
   * Read from the DOM rather than a ref because the markup comes from Shopify's
   * `video_tag` filter, which owns the element's attributes.
   *
   * @returns {HTMLVideoElement | null}
   */
  get #video() {
    return this.refs.background?.querySelector('video') ?? null;
  }

  /**
   * The video carries `autoplay`, so this runs again on every `play` — the
   * browser can start it before or after this component upgrades.
   */
  #pauseForReducedMotion = () => {
    const video = this.#video;

    if (!video || !prefersReducedMotion()) return;

    video.autoplay = false;
    video.pause();
  };
}

if (!customElements.get('hero-component')) {
  customElements.define('hero-component', HeroComponent);
}
